import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import DashboardPagination from '../components/DashboardPagination';
import OfficialReportLayout, {
  OfficialField,
  OfficialSectionTitle,
  OfficialTableCell,
} from '../components/print/OfficialReportLayout';
import funcaoOrcamentariaService from '../services/funcaoOrcamentariaService';
import reportService from '../services/reportService';
import { UI_THEME } from '../ui/theme';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const ETAPA_OPTIONS = [
  { value: 'NAO_SELECIONADO', label: 'Não selecionado' },
  { value: 'RASCUNHO', label: 'Rascunho' },
  { value: 'PUBLICADO', label: 'Publicado' },
  { value: 'SELECIONADO', label: 'Selecionado' },
  { value: 'ARQUIVADO', label: 'Arquivado' },
];
const sortOptions = [
  { value: 'codigo,desc', label: 'Código (mais recente)' },
  { value: 'codigo,asc', label: 'Código (mais antigo)' },
  { value: 'titulo,asc', label: 'Título A-Z' },
  { value: 'instituicao,asc', label: 'Instituição A-Z' },
  { value: 'area,asc', label: 'Área de atuação A-Z' },
  { value: 'etapa,asc', label: 'Etapa A-Z' },
  { value: 'valor,desc', label: 'Maior valor solicitado' },
  { value: 'dataCriacao,desc', label: 'Cadastro mais recente' },
];

const money = (value?: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);
const etapaLabel = (value?: string) => ETAPA_OPTIONS.find((item) => item.value === value)?.label || value || 'Todas';

const ValueBars: React.FC<{ label: string; solicitado: number; contemplado: number; max: number }> = ({ label, solicitado, contemplado, max }) => {
  const pct = (v: number) => (max > 0 ? Math.max((v / max) * 100, v > 0 ? 1 : 0) : 0);
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-slate-600 dark:text-slate-300">{label}</div>
      <div className="flex items-center gap-2">
        <div className="h-3 flex-1 rounded bg-slate-100 dark:bg-slate-800">
          <div className="h-3 rounded bg-slate-400 dark:bg-slate-500" style={{ width: `${pct(solicitado)}%` }} />
        </div>
        <span className="w-28 text-right text-[11px] tabular-nums text-slate-600 dark:text-slate-300">{money(solicitado)}</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-3 flex-1 rounded bg-slate-100 dark:bg-slate-800">
          <div className="h-3 rounded bg-teal-600 dark:bg-teal-500" style={{ width: `${pct(contemplado)}%` }} />
        </div>
        <span className="w-28 text-right text-[11px] tabular-nums text-slate-600 dark:text-slate-300">{money(contemplado)}</span>
      </div>
    </div>
  );
};

const ChartLegend: React.FC = () => (
  <div className="flex gap-4 pt-1 text-[11px] text-slate-500 dark:text-slate-400">
    <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded bg-slate-400" /> Solicitado</span>
    <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded bg-teal-600" /> Contemplado (emendas)</span>
  </div>
);

const ReportsPreProjetosPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
  const [exportError, setExportError] = useState('');
  const [tipoRelatorio, setTipoRelatorio] = useState<'LISTAGEM' | 'GRAFICOS'>('LISTAGEM');
  const mostrarGraficos = tipoRelatorio === 'GRAFICOS';

  const isSaas = UI_THEME === 'saas';
  const primaryButtonClass = isSaas
    ? 'inline-flex items-center justify-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'inline-flex items-center justify-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-500';
  const secondaryButtonClass = 'inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
  const darkButtonClass = 'inline-flex items-center justify-center rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-600 dark:hover:bg-slate-500';
  const inputClass = 'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40 print:border-slate-300 print:bg-white print:text-slate-700';
  const labelClass = 'space-y-1 text-sm text-slate-600 dark:text-slate-300 print:text-slate-600';

  const page = Math.max(Number(searchParams.get('page') || '1'), 1);
  const size = PAGE_SIZE_OPTIONS.includes(Number(searchParams.get('size'))) ? Number(searchParams.get('size')) : 20;
  const sort = searchParams.get('sort') || 'codigo,desc';

  const filters = useMemo(() => ({
    exercicio: searchParams.get('exercicio') || '',
    etapa: searchParams.get('etapa') || '',
    funcaoCodigo: searchParams.get('funcaoCodigo') || '',
    dataInicio: searchParams.get('dataInicio') || '',
    dataFim: searchParams.get('dataFim') || '',
    selecionadoPor: searchParams.get('selecionadoPor') || '',
  }), [searchParams]);
  const [draftFilters, setDraftFilters] = useState(filters);
  const [draftSort, setDraftSort] = useState(sort);

  useEffect(() => {
    setDraftFilters(filters);
    setDraftSort(sort);
  }, [filters, sort]);

  const funcoesQuery = useQuery({
    queryKey: ['funcoes-orcamentarias'],
    queryFn: () => funcaoOrcamentariaService.list(),
    staleTime: 5 * 60 * 1000,
  });
  const funcoes = funcoesQuery.data ?? [];

  const reportQuery = useQuery({
    queryKey: ['relatorio-pre-projetos', filters, page, size, sort],
    queryFn: () => reportService.getPreProjetos({ ...filters, page: page - 1, size, sort }),
  });
  const resumoQuery = useQuery({
    queryKey: ['relatorio-pre-projetos-resumo', filters],
    queryFn: () => reportService.getPreProjetosResumo(filters),
    // Os gráficos somam valores: sem exercício somariam todos os anos juntos.
    enabled: mostrarGraficos && !!filters.exercicio,
  });
  const resumo = resumoQuery.data;
  const items = reportQuery.data?.items ?? [];
  const totalItems = reportQuery.data?.totalItems ?? 0;
  const loading = reportQuery.isLoading;
  const error = reportQuery.isError
    ? (reportQuery.error as any)?.response?.data?.error || 'Não foi possível carregar o relatório de pré-projetos.'
    : exportError;

  const areaLabel = (codigo?: string) => (codigo ? funcoes.find((f) => f.codigo === codigo)?.descricao || codigo : 'Todas');

  const updateParams = (updates: Record<string, string>, resetPage = true) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    if (resetPage) {
      params.set('page', '1');
    }
    setSearchParams(params);
  };

  const handleVisualizar = () => {
    const params = new URLSearchParams(searchParams);
    Object.entries(draftFilters).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    params.set('sort', draftSort);
    params.set('page', '1');
    params.set('size', String(size));
    setSearchParams(params);
  };

  const clearFilters = () => {
    setDraftFilters({ exercicio: '', etapa: '', funcaoCodigo: '', dataInicio: '', dataFim: '', selecionadoPor: '' });
    setDraftSort('codigo,desc');
    setSearchParams(new URLSearchParams({ page: '1', size: String(size), sort: 'codigo,desc' }));
  };

  const handleExport = async (type: 'xlsx' | 'pdf') => {
    try {
      if (type === 'pdf' && mostrarGraficos && !filters.exercicio) {
        setExportError('Informe o exercício e clique em Visualizar para exportar os gráficos.');
        return;
      }
      setExporting(type);
      setExportError('');
      if (type === 'xlsx') {
        await reportService.exportPreProjetosXlsx({ ...filters, sort });
      } else {
        await reportService.exportPreProjetosPdf({ ...filters, sort }, mostrarGraficos);
      }
    } catch (err: any) {
      // O download é blob: em erro o corpo JSON chega como Blob e precisa ser lido.
      let message = '';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text())?.error || '';
        } catch {
          message = '';
        }
      } else {
        message = data?.error || '';
      }
      setExportError(message || `Não foi possível exportar o arquivo ${type.toUpperCase()}.`);
    } finally {
      setExporting(null);
    }
  };

  const summaryFields = [
    <OfficialField key="total" label="Registros filtrados" value={String(totalItems)} />,
    <OfficialField key="pagina" label="Registros na página" value={String(items.length)} />,
    <OfficialField key="valor" label="Valor solicitado (página)" value={money(items.reduce((sum, item) => sum + (item.valorSolicitado ?? 0), 0))} />,
    <OfficialField key="area" label="Com área de atuação" value={String(items.filter((item) => item.areaAtuacao).length)} />,
  ];

  return (
    <OfficialReportLayout
      moduleLabel="Pré-projetos"
      title="Relatório de pré-projetos"
      subtitle="Consulta consolidada dos pré-projetos das instituições, com área de atuação, etapa, exercício e valor solicitado."
      summaryFields={summaryFields}
      toolbar={
        <>
          <button type="button" onClick={handleVisualizar} className={primaryButtonClass}>
            Visualizar
          </button>
          <button
            type="button"
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null || mostrarGraficos}
            title={mostrarGraficos ? 'A planilha traz a relação de pré-projetos; escolha esse tipo de relatório.' : undefined}
            className={secondaryButtonClass}
          >
            {exporting === 'xlsx' ? 'Exportando XLSX...' : 'Exportar XLSX'}
          </button>
          <button
            type="button"
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null}
            className={darkButtonClass}
          >
            {exporting === 'pdf' ? 'Exportando PDF...' : 'Exportar PDF'}
          </button>
          <button type="button" onClick={clearFilters} className={secondaryButtonClass}>
            Limpar filtros
          </button>
        </>
      }
    >
      <OfficialSectionTitle number="1" title="Filtros de consulta" aside="A visualização abaixo reflete somente os parâmetros aplicados." />
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <label className={labelClass}>
          <span className="font-medium">Exercício</span>
          <input
            type="number"
            min={2000}
            max={2100}
            value={draftFilters.exercicio}
            onChange={(e) => setDraftFilters((current) => ({ ...current, exercicio: e.target.value }))}
            className={inputClass}
            placeholder="Todos"
          />
        </label>
        <label className={labelClass}>
          <span className="font-medium">Etapa</span>
          <select
            value={draftFilters.etapa}
            onChange={(e) => setDraftFilters((current) => ({ ...current, etapa: e.target.value }))}
            className={inputClass}
          >
            <option value="">Todas</option>
            {ETAPA_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Área de atuação</span>
          <select
            value={draftFilters.funcaoCodigo}
            onChange={(e) => setDraftFilters((current) => ({ ...current, funcaoCodigo: e.target.value }))}
            className={inputClass}
          >
            <option value="">Todas</option>
            {funcoes.map((funcao) => (
              <option key={funcao.codigo} value={funcao.codigo}>{funcao.descricao}</option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Data inicial</span>
          <input
            type="date"
            value={draftFilters.dataInicio}
            onChange={(e) => setDraftFilters((current) => ({ ...current, dataInicio: e.target.value }))}
            className={inputClass}
          />
        </label>
        <label className={labelClass}>
          <span className="font-medium">Data final</span>
          <input
            type="date"
            value={draftFilters.dataFim}
            onChange={(e) => setDraftFilters((current) => ({ ...current, dataFim: e.target.value }))}
            className={inputClass}
          />
        </label>
        <label className={labelClass}>
          <span className="font-medium">Selecionado por</span>
          <input
            type="text"
            value={draftFilters.selecionadoPor}
            onChange={(e) => setDraftFilters((current) => ({ ...current, selecionadoPor: e.target.value }))}
            className={inputClass}
            placeholder="Nome do vereador"
          />
        </label>
        <label className={labelClass}>
          <span className="font-medium">Ordenação</span>
          <select
            value={draftSort}
            onChange={(e) => setDraftSort(e.target.value)}
            className={inputClass}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Tipo de relatório</span>
          <select
            value={tipoRelatorio}
            onChange={(e) => {
              const tipo = e.target.value as 'LISTAGEM' | 'GRAFICOS';
              setTipoRelatorio(tipo);
              if (tipo === 'GRAFICOS' && !filters.exercicio) {
                updateParams({ exercicio: String(new Date().getFullYear()) });
              }
            }}
            className={inputClass}
          >
            <option value="LISTAGEM">Relação de pré-projetos</option>
            <option value="GRAFICOS">Gráficos de valores</option>
          </select>
        </label>
      </div>

      <OfficialSectionTitle number="2" title="Parâmetros aplicados" />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        <OfficialField label="Selecionado por" value={filters.selecionadoPor || 'Todos'} />
        <OfficialField label="Exercício" value={filters.exercicio || 'Todos'} />
        <OfficialField label="Etapa" value={etapaLabel(filters.etapa)} />
        <OfficialField label="Área de atuação" value={areaLabel(filters.funcaoCodigo)} />
        <OfficialField label="Data inicial" value={filters.dataInicio ? new Date(`${filters.dataInicio}T00:00:00`).toLocaleDateString('pt-BR') : 'Todos'} />
        <OfficialField label="Data final" value={filters.dataFim ? new Date(`${filters.dataFim}T00:00:00`).toLocaleDateString('pt-BR') : 'Todos'} />
      </div>

      {mostrarGraficos && (
      <>
      <OfficialSectionTitle number="3" title="Valores por área de atuação" aside="Solicitado × contemplado em emendas, conforme os filtros aplicados." />
      {!filters.exercicio ? (
        <div className="rounded-xl border border-slate-200 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Informe o exercício e clique em Visualizar: os gráficos são sempre de um único exercício.
        </div>
      ) : resumo && resumo.porArea.length > 0 ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Saúde × demais áreas</div>
            {[
              { label: 'Saúde', solicitado: resumo.saudeSolicitado, contemplado: resumo.saudeContemplado },
              { label: 'Demais áreas', solicitado: resumo.demaisSolicitado, contemplado: resumo.demaisContemplado },
            ].map((grupo) => (
              <ValueBars key={grupo.label} label={grupo.label} solicitado={grupo.solicitado} contemplado={grupo.contemplado}
                max={Math.max(resumo.saudeSolicitado, resumo.demaisSolicitado, resumo.saudeContemplado, resumo.demaisContemplado)} />
            ))}
            <ChartLegend />
          </div>
          <div className="space-y-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-700 lg:col-span-2">
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Por área de atuação</div>
            {resumo.porArea.map((a) => (
              <ValueBars key={a.area} label={`${a.area} (${a.quantidade})`} solicitado={a.valorSolicitado} contemplado={a.valorContemplado}
                max={Math.max(...resumo.porArea.flatMap((x) => [x.valorSolicitado, x.valorContemplado]))} />
            ))}
            <ChartLegend />
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Sem valores para exibir nos filtros informados.
        </div>
      )}

      <OfficialSectionTitle number="4" title="Gasto em emendas por parlamentar" aside="Soma das emendas dos pré-projetos selecionados por cada parlamentar." />
      {!filters.exercicio ? null : resumo && resumo.porParlamentar.length > 0 ? (
        <div className="space-y-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
          {resumo.porParlamentar.map((p) => {
            const max = Math.max(...resumo.porParlamentar.map((x) => x.valorContemplado));
            const pct = max > 0 ? Math.max((p.valorContemplado / max) * 100, p.valorContemplado > 0 ? 1 : 0) : 0;
            return (
              <div key={p.parlamentar} className="space-y-1">
                <div className="text-xs font-medium text-slate-600 dark:text-slate-300">{p.parlamentar} ({p.quantidade})</div>
                <div className="flex items-center gap-2">
                  <div className="h-3 flex-1 rounded bg-slate-100 dark:bg-slate-800">
                    <div className="h-3 rounded bg-teal-600 dark:bg-teal-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-28 text-right text-[11px] tabular-nums text-slate-600 dark:text-slate-300">{money(p.valorContemplado)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Nenhum pré-projeto selecionado nos filtros informados.
        </div>
      )}
      </>
      )}

      {!mostrarGraficos && (
      <>
      <OfficialSectionTitle number="3" title="Relação de pré-projetos" />
      {loading ? (
        <div className="rounded-xl border border-slate-200 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Carregando relatório de pré-projetos...
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Nenhum pré-projeto encontrado para os filtros informados.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <OfficialTableCell head>Código</OfficialTableCell>
                  <OfficialTableCell head>Título</OfficialTableCell>
                  <OfficialTableCell head>Instituição</OfficialTableCell>
                  <OfficialTableCell head>Área de atuação</OfficialTableCell>
                  <OfficialTableCell head>Tipo</OfficialTableCell>
                  <OfficialTableCell head align="center">Exercício</OfficialTableCell>
                  <OfficialTableCell head>Etapa</OfficialTableCell>
                  <OfficialTableCell head>Selecionado por</OfficialTableCell>
                  <OfficialTableCell head align="right">Valor solicitado</OfficialTableCell>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <OfficialTableCell className="whitespace-nowrap">{item.codigo || 'Não informado'}</OfficialTableCell>
                    <OfficialTableCell>
                      <div className="font-semibold">{item.titulo || 'Não informado'}</div>
                      {item.objeto ? (
                        <div className="mt-1 line-clamp-3 text-[11px] text-slate-500 dark:text-slate-400">{item.objeto}</div>
                      ) : null}
                    </OfficialTableCell>
                    <OfficialTableCell>{item.instituicaoNome || 'Não informado'}</OfficialTableCell>
                    <OfficialTableCell>{item.areaAtuacao || 'Não informado'}</OfficialTableCell>
                    <OfficialTableCell>{item.classificacao || 'Não informado'}</OfficialTableCell>
                    <OfficialTableCell align="center">{item.exercicio ?? 'Não informado'}</OfficialTableCell>
                    <OfficialTableCell>{etapaLabel(item.etapa)}</OfficialTableCell>
                    <OfficialTableCell>
                      {item.selecionadoPor ? (
                        <>
                          <div>{item.selecionadoPor}</div>
                          {item.dataSelecao ? (
                            <div className="text-[11px] text-slate-500 dark:text-slate-400">
                              {new Date(item.dataSelecao).toLocaleDateString('pt-BR')}
                            </div>
                          ) : null}
                        </>
                      ) : '—'}
                    </OfficialTableCell>
                    <OfficialTableCell align="right" className="whitespace-nowrap">{money(item.valorSolicitado)}</OfficialTableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DashboardPagination
            page={page}
            pageSize={size}
            totalItems={totalItems}
            onPageChange={(value) => updateParams({ page: String(value) }, false)}
            onPageSizeChange={(value) => {
              const params = new URLSearchParams(searchParams);
              params.set('size', String(value));
              params.set('page', '1');
              setSearchParams(params);
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            itemLabel="pré-projetos"
          />
        </div>
      )}
      </>
      )}
    </OfficialReportLayout>
  );
};

export default ReportsPreProjetosPage;
